import React, { useState, useEffect } from 'react';
import { UserCheck, LogOut, Flame, Clock, ShieldCheck, ChevronRight, Users } from 'lucide-react';

export default function IdleScreen({ onCheckIn, onCheckOut, onOpenEvacuation, onStaffAttendance }) {
  const [time, setTime] = useState(new Date());
  const [staffCount, setStaffCount] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchCount = async () => {
      try {
        const res = await fetch('/api/visitors/staff-attendance/status');
        if (res.ok) {
          const data = await res.json();
          if (mounted && data.staff) {
            const onSite = data.staff.filter((s) => s.status === 'Signed In').length;
            setStaffCount({ onSite, total: data.staff.length });
          }
        }
      } catch (e) {
        // ignore fetch error on idle screen
      }
    };
    fetchCount();
    const countTimer = setInterval(fetchCount, 20000);
    return () => {
      mounted = false;
      clearInterval(countTimer);
    };
  }, []);

  const timeString = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateString = time.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="flex-1 flex flex-col justify-between p-6 sm:p-12 max-w-5xl mx-auto w-full">
      {/* Top Bar / Time Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-8 border-b border-white/10">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-300 tracking-tight font-display">
              JSD IT
            </span>
            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-cyan-950 text-cyan-400 border border-cyan-800/60">
              Reception Kiosk
            </span>
            <span className="px-1.5 py-0.5 text-[10px] font-mono font-bold rounded bg-slate-800 text-slate-400 border border-slate-700">
              v3.1
            </span>
          </div>
          <p className="text-xs text-slate-400 font-medium">Head Office Visitor Management & Sign-In</p>
        </div>

        {/* Digital Clock */}
        <div className="flex items-center gap-3 bg-slate-900/80 px-5 py-3 rounded-2xl border border-white/10 shadow-lg">
          <Clock className="w-5 h-5 text-cyan-400 animate-pulse" />
          <div className="text-right">
            <div className="text-xl sm:text-2xl font-mono font-bold text-white tracking-wider">
              {timeString}
            </div>
            <div className="text-[11px] text-slate-400 font-medium">
              {dateString}
            </div>
          </div>
        </div>
      </div>

      {/* Hero Welcome & Dual Big Cards */}
      <div className="my-auto py-8 space-y-10">
        <div className="text-center space-y-3 max-w-2xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white font-display">
            Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-300">JSD IT</span>
          </h1>
          <p className="text-slate-400 text-lg sm:text-xl font-medium">
            Please sign in to let your host know you have arrived.
          </p>
        </div>

        {/* Two Giant Touch Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Card 1: Check In */}
          <button
            type="button"
            onClick={onCheckIn}
            className="group relative overflow-hidden rounded-3xl p-8 sm:p-10 text-left bg-gradient-to-b from-slate-850 to-slate-900 border-2 border-cyan-500/30 hover:border-cyan-400 active:scale-[0.98] transition-all duration-200 shadow-2xl shadow-cyan-950/40 flex flex-col justify-between min-h-[260px] touch-btn"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 group-hover:scale-110 transition-all duration-300 text-cyan-400 pointer-events-none">
              <UserCheck className="w-36 h-36" />
            </div>

            <div className="space-y-4 relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400 group-hover:bg-cyan-500 group-hover:text-slate-950 transition-colors shadow-lg">
                <UserCheck className="w-9 h-9" />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                  Check In
                </h3>
                <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                  Arriving for a meeting, appointment, or delivery.
                </p>
              </div>
            </div>

            <div className="pt-6 relative z-10 flex items-center justify-between text-cyan-400 font-bold text-base group-hover:translate-x-1 transition-transform">
              <span>Start Sign In</span>
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          </button>

          {/* Card 2: Check Out */}
          <button
            type="button"
            onClick={onCheckOut}
            className="group relative overflow-hidden rounded-3xl p-8 sm:p-10 text-left bg-gradient-to-b from-slate-850 to-slate-900 border-2 border-amber-500/30 hover:border-amber-400 active:scale-[0.98] transition-all duration-200 shadow-2xl shadow-amber-950/40 flex flex-col justify-between min-h-[260px] touch-btn"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 group-hover:scale-110 transition-all duration-300 text-amber-400 pointer-events-none">
              <LogOut className="w-36 h-36" />
            </div>

            <div className="space-y-4 relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 group-hover:bg-amber-500 group-hover:text-slate-950 transition-colors shadow-lg">
                <LogOut className="w-9 h-9" />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                  Check Out
                </h3>
                <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                  Leaving the premises. Tap to sign out.
                </p>
              </div>
            </div>

            <div className="pt-6 relative z-10 flex items-center justify-between text-amber-400 font-bold text-base group-hover:translate-x-1 transition-transform">
              <span>Sign Out</span>
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          </button>
        </div>

        {/* Staff Sign-In / Attendance Action Bar */}
        <div className="max-w-4xl mx-auto w-full pt-2">
          <button
            type="button"
            onClick={onStaffAttendance}
            className="group w-full relative overflow-hidden rounded-3xl p-5 sm:p-6 bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 border-2 border-slate-700/60 hover:border-cyan-400 active:scale-[0.99] transition-all duration-200 shadow-xl flex items-center justify-between touch-btn"
          >
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400 group-hover:bg-cyan-500 group-hover:text-slate-950 transition-colors shadow-md">
                <Users className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2.5">
                  <h4 className="text-lg sm:text-xl font-black text-white font-display">
                    Staff Attendance
                  </h4>
                  {staffCount && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
                      {staffCount.onSite} On Site
                    </span>
                  )}
                </div>
                <p className="text-slate-400 text-xs sm:text-sm mt-0.5 font-medium">
                  Single-tap sign in / out for staff & engineers
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-cyan-400 font-bold text-sm group-hover:translate-x-1 transition-transform relative z-10">
              <span className="hidden sm:inline">Tap to Open</span>
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-cyan-400" />
          <span>All visitors must be accompanied by an authorised staff member.</span>
        </div>

        {/* Emergency Evacuation Button */}
        <button
          type="button"
          onClick={onOpenEvacuation}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-950/40 hover:bg-red-900/60 border border-red-800/40 text-red-300 transition-colors font-medium touch-btn"
        >
          <Flame className="w-3.5 h-3.5 text-red-400" />
          <span>Emergency Roll Call</span>
        </button>
      </div>
    </div>
  );
}
